import { type V1ApiResponse } from '@webapp/core/interfaces/api/V1ApiResponse'
import {
  throwIfV1Failure,
  throwRepoErrorFromApiV1Error,
} from '@webapp/core/utils/repositories/throwRepoErrorFromApiV1Error'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type CatalogPermissionSecondaryCategoryConfigConstructor } from '@webapp/modules/purchasing/entities/CatalogPermissionSecondaryCategoryConfig'
import { type CatalogPermissionUserConfigConstructor } from '@webapp/modules/purchasing/entities/CatalogPermissionUserConfig'
import { type ICatalogAccessRepository } from '../../../business-logic/interfaces/ICatalogAccessRepository'
import { type ListCatalogConfigData } from '../../../business-logic/types/ListCatalogConfigData'
import { type CatalogPermissionDeptConfigApiModel } from '../../api-models/CatalogPermissionDeptConfigApiModel'
import { type CatalogPermissionUserConfigApiModel } from '../../api-models/CatalogPermissionUserConfigApiModel'
import { mapCatalogPermissionDeptConfigApiModel } from '../../mappers/mapCatalogPermissionDeptConfigApiModel'
import { mapCatalogPermissionUserConfigApiModel } from '../../mappers/mapCatalogPermissionUserConfigApiModel'
import { mapSaveDepartmentGrantsPayload } from '../../mappers/mapSaveDepartmentGrantsPayload'
import { mapSaveUserGrantsPayload } from '../../mappers/mapSaveUserGrantsPayload'
import { type V1PaginatedResponse } from '../../types/V1PaginatedResponse'

interface Dependency {
  httpClient: IHttpClient
}

export class CatalogAccessRepository implements ICatalogAccessRepository {
  constructor(private readonly deps: Dependency) {}

  async getCatalogPermissionEnabled() {
    try {
      const res = await this.deps.httpClient.get<V1ApiResponse<boolean>>(
        `/api/permission/catalog/`,
        {
          params: { catalog_permission_setting: true },
        }
      )

      throwIfV1Failure(res.data.success, res.data)

      return res.data.data
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromApiV1Error(err)
    }
  }

  async listUserConfigs(
    params: ListCatalogConfigData
  ): Promise<RepoPaginatedResponse<CatalogPermissionUserConfigConstructor>> {
    try {
      const res = await this.deps.httpClient.get<
        V1PaginatedResponse<CatalogPermissionUserConfigApiModel>
      >(`/api/permission/catalog/`, {
        params: {
          catalog_permission: 'user',
          search: params.search,
          page: params.page,
          limit: params.limit,
          offset: params.offset,
          order_by: params.orderBy,
          sort: params.sort,
        },
      })

      throwIfV1Failure(res.data.success, res.data)

      const { data, paging } = res.data

      return {
        data: data.map((item) => mapCatalogPermissionUserConfigApiModel(item)),
        pagination: {
          count: paging.count,
          page: paging.page,
          pageSize: paging.limit,
          numPages:
            paging.limit > 0 ? Math.ceil(paging.count / paging.limit) : 0,
        },
      }
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromApiV1Error(err)
    }
  }

  async listSecondaryCategoryConfigs(
    params: ListCatalogConfigData
  ): Promise<
    RepoPaginatedResponse<CatalogPermissionSecondaryCategoryConfigConstructor>
  > {
    try {
      const res = await this.deps.httpClient.get<
        V1PaginatedResponse<CatalogPermissionDeptConfigApiModel>
      >(`/api/permission/catalog/`, {
        params: {
          catalog_permission: 'department',
          search: params.search,
          page: params.page,
          limit: params.limit,
          offset: params.offset,
          order_by: params.orderBy,
          sort: params.sort,
        },
      })

      throwIfV1Failure(res.data.success, res.data)

      const { data, paging } = res.data

      return {
        data: data.map((item) => mapCatalogPermissionDeptConfigApiModel(item)),
        pagination: {
          count: paging.count,
          page: paging.page,
          pageSize: paging.limit,
          numPages:
            paging.limit > 0 ? Math.ceil(paging.count / paging.limit) : 0,
        },
      }
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromApiV1Error(err)
    }
  }

  async setRfoLockDefault(enabled: boolean) {
    try {
      // v3 endpoint: the request body is a raw JSON boolean (`true`/`false`,
      // not an object), so it must be pre-serialized — axios drops a bare
      // boolean `data` and sends an empty body, which the API rejects (400).
      // Response: HTTP 201, body `{ data: <bool>, metadata }` — no envelope.
      const res = await this.deps.httpClient.post<{ data: boolean }>(
        `/api/v3/catalog-items/settings/rfo-lock/`,
        JSON.stringify(enabled),
        { headers: { 'Content-Type': 'application/json' } }
      )

      return res.data.data
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  private async setRfoLockAll(value: boolean) {
    try {
      await this.deps.httpClient.post(`/api/v3/catalog-items/rfo-lock/`, {
        rfo_lock: value,
      })
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async lockAllCatalogItems() {
    return this.setRfoLockAll(true)
  }

  async unlockAllCatalogItems() {
    return this.setRfoLockAll(false)
  }

  async setCatalogPermissionEnabled(enabled: boolean) {
    try {
      const res = await this.deps.httpClient.post<V1ApiResponse<unknown>>(
        `/api/permission/catalog/`,
        { catalog_permission_setting: enabled }
      )

      throwIfV1Failure(res.data.success, res.data)
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromApiV1Error(err)
    }
  }

  async saveUserGrants(
    userId: number,
    secondaryCategoryIds: number[]
  ): Promise<void> {
    try {
      const payload = mapSaveUserGrantsPayload(userId, secondaryCategoryIds)
      const res = await this.deps.httpClient.post<V1ApiResponse<unknown>>(
        `/api/permission/catalog/`,
        payload
      )

      throwIfV1Failure(res.data.success, res.data)
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromApiV1Error(err)
    }
  }

  async saveSecondaryCategoryGrants(
    secondaryCategoryId: number,
    userIds: number[]
  ): Promise<void> {
    try {
      const payload = mapSaveDepartmentGrantsPayload(
        secondaryCategoryId,
        userIds
      )
      const res = await this.deps.httpClient.post<V1ApiResponse<unknown>>(
        `/api/permission/catalog/`,
        payload
      )

      throwIfV1Failure(res.data.success, res.data)
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromApiV1Error(err)
    }
  }
}
